import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Menu, X, Home, Trophy, FileText, BookOpen, Users, Building2, MapPin, CheckSquare, LogOut, UserCircle, UserPlus, Clock, Mail, Globe } from 'lucide-react';
import { Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';

export default function DashboardLayout({ children }) {
  const [user, setUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [testingMode, setTestingMode] = useState({ enabled: false, email: '' });

  useEffect(() => {
    const load = () => {
      axios.get(`${API}/settings/testing-mode`, { withCredentials: true })
        .then(r => setTestingMode({
          enabled: !!r.data.testing_mode_enabled,
          email: r.data.testing_email_address || '',
        }))
        .catch(() => {});
    };
    load();
    const listener = () => load();
    window.addEventListener('testing-mode-changed', listener);
    return () => window.removeEventListener('testing-mode-changed', listener);
  }, []);
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(response.data);
      } catch (error) {
        console.error('Failed to fetch user:', error);
      }
    };
    fetchUser();
  }, [location.pathname]);
  
  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      toast.success('Logged out successfully');
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/');
    }
  };
  
  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: Home },
    { path: '/competitions', label: 'Competitions', icon: Trophy },
    { path: '/competition-pages', label: 'Competition Pages', icon: Globe },
    { path: '/documents', label: 'Documents', icon: FileText },
    { path: '/skaters-entries', label: 'Skaters & Entries', icon: UserPlus },
  ];
  
  // Items shown to ALL users under Admin section. As of Feb 2026, the
  // legacy trio of People, Venue Management and Clubs & Organisations
  // is consolidated into a single "People & Places" console.
  const adminItemsAll = [
    { path: '/admin/people-places', label: 'People & Places', icon: Users },
    { path: '/content', label: 'Content Library', icon: BookOpen },
    { path: '/event-timing', label: 'Event Timing', icon: Clock },
  ];
  
  // Items shown to SUPER USERS only under Admin section
  const adminItemsSuper = [
    { path: '/admin/approvals', label: 'Approval Requests', icon: CheckSquare },
    { path: '/admin/email-delivery', label: 'Email Delivery', icon: Mail },
    { path: '/admin/settings', label: 'System Settings', icon: Settings },
  ];
  
  return (
    <div className="min-h-screen bg-background">
      {testingMode.enabled && (
        <div
          className="w-full bg-red-600 text-white text-center py-2 text-sm font-semibold sticky top-0 z-50"
          data-testid="testing-mode-banner"
        >
          ⚠ TESTING MODE ACTIVE - all emails are being redirected to {testingMode.email} - TESTING MODE ACTIVE ⚠
        </div>
      )}
      {/* Pending-club-approval lockout — user has a session but can't
          use the app until a super approves their new club record. */}
      {user?.account_status === 'pending_club_approval' && (
        <div className="min-h-screen flex items-center justify-center px-4" data-testid="pending-approval-lockout">
          <div className="max-w-md p-8 bg-white rounded-lg shadow-lg text-center">
            <div className="text-5xl mb-4">⏳</div>
            <h1 className="text-2xl font-bold mb-3" style={{ color: '#2D4A3E' }}>Waiting for approval</h1>
            <p className="text-muted-foreground mb-6">
              Your account has been created pending confirmation of your club details.
              You'll receive a notification once that's been approved.
            </p>
            <Button variant="outline" onClick={handleLogout} data-testid="pending-logout-button">
              Sign out
            </Button>
          </div>
        </div>
      )}
      {user?.account_status !== 'pending_club_approval' && (<>
      <header className="bg-white border-b border-stone-200 sticky top-0 z-40 backdrop-blur-md bg-white/80">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              data-testid="menu-toggle-button"
              className="lg:hidden"
            >
              {sidebarOpen ? <X /> : <Menu />}
            </Button>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif', color: '#2D4A3E' }}>
              CompCreator
            </h1>
          </div>
          
          <div className="flex items-center gap-4">
            {user && (
              <div className="flex items-center gap-3">
                <Avatar>
                  <AvatarImage src={user.picture} />
                  <AvatarFallback className="bg-primary text-primary-foreground">
                    {user.name?.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="hidden md:block text-right">
                  <p className="text-sm font-medium">{user.name}</p>
                  <p className="text-xs text-muted-foreground capitalize">{user.role} User</p>
                </div>
                <Button
                  variant="ghost"
                  className="gap-2"
                  onClick={() => navigate('/profile')}
                  data-testid="profile-button"
                  title="My Profile"
                >
                  <UserCircle className="h-4 w-4" />
                  <span className="hidden lg:inline">My Profile</span>
                </Button>
                <Button
                  variant="ghost"
                  className="gap-2"
                  onClick={handleLogout}
                  data-testid="logout-button"
                >
                  <LogOut className="h-4 w-4" />
                  <span className="hidden lg:inline">Sign Out</span>
                </Button>
              </div>
            )}
          </div>
        </div>
      </header>
      
      <div className="flex">
        <aside
          className={`
            fixed lg:sticky top-[73px] left-0 h-[calc(100vh-73px)] w-64 bg-white border-r border-stone-200 z-30
            transform transition-transform duration-300 ease-in-out
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          `}
        >
          <nav className="p-4 space-y-1">
            {navItems.map(item => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`sidebar-item flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium ${isActive ? 'active' : ''}`}
                  style={{ color: isActive ? '#2D4A3E' : '#6B7280' }}
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon className="h-5 w-5" />
                  {item.label}
                </Link>
              );
            })}
            
            {/* Admin Section - visible to ALL users */}
            <div className="pt-4 mt-4 border-t border-stone-200">
              <p className="px-4 text-xs font-semibold text-muted-foreground mb-2">ADMIN</p>
            </div>
            {adminItemsAll.map(item => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`sidebar-item flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium ${isActive ? 'active' : ''}`}
                  style={{ color: isActive ? '#2D4A3E' : '#6B7280' }}
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon className="h-5 w-5" />
                  {item.label}
                </Link>
              );
            })}
            
            {/* Super User only admin items */}
            {user?.role === 'super' && (
              <>
                {adminItemsSuper.map(item => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      className={`sidebar-item flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium ${isActive ? 'active' : ''}`}
                      style={{ color: isActive ? '#2D4A3E' : '#6B7280' }}
                      onClick={() => setSidebarOpen(false)}
                    >
                      <Icon className="h-5 w-5" />
                      {item.label}
                    </Link>
                  );
                })}
              </>
            )}
          </nav>
        </aside>
        
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/20 z-20 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
        
        <main className="flex-1 p-6 md:p-8 lg:p-12 lg:ml-0">
          {children}
        </main>
      </div>
      </>)}
    </div>
  );
}
